<script lang="ts" setup>
import { ref, onMounted } from 'vue';

export interface ToastMessage {
    id: number;
    severity: 'success' | 'error' | 'info' | 'warn';
    summary: string;
    detail: string;
    life?: number;
}

const messages = ref<ToastMessage[]>([]);
let messageIdCounter = 0;

function add(message: Omit<ToastMessage, 'id'>) {
    const id = messageIdCounter++;
    const newMessage: ToastMessage = {
        id,
        ...message,
        life: message.life || 3000
    };
    
    messages.value.push(newMessage);
    
    if (newMessage.life) {
        setTimeout(() => {
            remove(id);
        }, newMessage.life);
    }
}

function remove(id: number) {
    const index = messages.value.findIndex(m => m.id === id);
    if (index > -1) {
        messages.value.splice(index, 1);
    }
}

defineExpose({ add });
</script>

<template>
    <div class="custom-toast-container">
        <transition-group name="toast">
            <div
                v-for="message in messages"
                :key="message.id"
                class="custom-toast"
                :class="{
                    'toast-success': message.severity === 'success',
                    'toast-error': message.severity === 'error',
                    'toast-info': message.severity === 'info',
                    'toast-warn': message.severity === 'warn'
                }"
            >
                <div class="toast-content">
                    <i
                        :class="message.severity === 'success' ? 'pi pi-check-circle' : message.severity === 'error' ? 'pi pi-times-circle' : 'pi pi-info-circle'"
                        class="toast-icon"
                    ></i>
                    <div class="toast-text">
                        <span class="toast-summary">{{ message.summary }}</span>
                        <span class="toast-detail">{{ message.detail }}</span>
                    </div>
                    <button class="toast-close" @click="remove(message.id)">
                        <i class="pi pi-times"></i>
                    </button>
                </div>
            </div>
        </transition-group>
    </div>
</template>

<style scoped>
.custom-toast-container {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
}

.custom-toast {
    width: 500px;
    min-height: 80px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    pointer-events: all;
}

.toast-success {
    background-color: black;
}

.toast-error {
    background-color: #f04438;
}

.toast-info {
    background-color: #1966FF;
}

.toast-warn {
    background-color: #f79009;
}

.toast-content {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    color: white;
}

.toast-icon {
    font-size: 24px;
    flex-shrink: 0;
}

.toast-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.toast-summary {
    font-weight: 600;
    font-size: 14px;
}

.toast-detail {
    font-size: 13px;
}

.toast-close {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.toast-close:hover {
    opacity: 1;
}

/* Animations */
.toast-enter-active,
.toast-leave-active {
    transition: all 0.3s ease;
}

.toast-enter-from {
    opacity: 0;
    transform: translateY(20px);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100px);
}

.toast-move {
    transition: transform 0.3s ease;
}
</style>
